import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ClipboardList, UserCheck, Bot, Clock } from "lucide-react";
import { api, formatDate } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { ComparablesTable } from "./ComparablesTable";
import { MarketSummaryChart } from "./MarketSummaryChart";
import { BudgetHistory } from "./BudgetHistory";
import { DiagnosisRegenCard } from "./DiagnosisRegenCard";
import { DiagnosisCompare } from "./DiagnosisCompare";

const CLASS_LABEL = {
  apto_avanzar: "Puede avanzar a estructuración",
  apto_con_ajustes: "Puede avanzar con ajustes",
  requiere_reformulacion: "Requiere reformulación",
};
const MOD_LABEL = {
  licitacion_publica: "Licitación pública", seleccion_abreviada: "Selección abreviada",
  concurso_meritos: "Concurso de méritos", contratacion_directa: "Contratación directa",
  minima_cuantia: "Mínima cuantía",
};
const LEVEL_CLS = { alta: "text-rose-700", alto: "text-rose-700", critico: "text-rose-800 font-semibold", media: "text-amber-700", medio: "text-amber-700", baja: "text-gray-600", bajo: "text-gray-600" };

function Block({ title, children, testId }) {
  return (
    <div className="py-5 border-t border-gray-100 first:border-t-0" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">{title}</p>
      {children}
    </div>
  );
}

function ReviewBadge({ review }) {
  const icon = review.status === "revisado" ? <UserCheck className="w-4 h-4" /> : review.status === "automatizado" ? <Bot className="w-4 h-4" /> : <Clock className="w-4 h-4" />;
  const cls = review.status === "revisado" ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900";
  return (
    <div className={`border p-3 text-xs flex gap-2 ${cls}`} data-testid="diagnosis-review-badge">
      {icon}
      <div>
        <p className="font-medium" data-testid="diagnosis-review-status">{review.label}</p>
        {review.status === "revisado" && (
          <p className="mt-0.5">
            {review.reviewer_name} · {formatDate(review.reviewed_at)} · versión v{review.version}
            {review.comments && <span className="block mt-1 italic">“{review.comments}”</span>}
          </p>
        )}
      </div>
    </div>
  );
}

function Risks({ risks }) {
  return (
    <ul className="space-y-3">
      {risks.map((r, i) => (
        <li key={i} className="border-l-2 border-gray-200 pl-3" data-testid={`diagnosis-risk-${i}`}>
          <p className="text-sm text-gray-900">{r.descripcion}</p>
          <p className="text-xs mt-1">
            Probabilidad <span className={LEVEL_CLS[r.probabilidad]}>{r.probabilidad}</span> · Impacto <span className={LEVEL_CLS[r.impacto]}>{r.impacto}</span>
          </p>
          <p className="text-xs text-gray-600 mt-1"><strong>Mitigación propuesta:</strong> {r.mitigacion}</p>
        </li>
      ))}
    </ul>
  );
}

const VERIF_CLS = {
  validada: "bg-emerald-50 text-emerald-800 border-emerald-300",
  lista_base: "bg-gray-100 text-gray-700 border-gray-300",
  no_registrada: "bg-rose-50 text-rose-800 border-rose-300",
};

function Normas({ normas }) {
  if (!normas.length) return <p className="text-sm text-gray-600">No se identificaron normas específicas; requiere revisión.</p>;
  return (
    <ul className="space-y-3">
      {normas.map((n, i) => (
        <li key={i} className="text-sm border-l-2 border-gray-200 pl-3" data-testid={`diagnosis-norma-${i}`}>
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-gray-900">{n.alias}</strong>
            <span className={`border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${VERIF_CLS[n.verification_status]}`} data-testid={`norma-verification-${i}`}>
              {n.verification_label}
            </span>
          </div>
          <p className="text-xs text-gray-700 mt-1" data-testid={`norma-articulos-${i}`}>
            Artículos citados: {n.articulos?.length ? n.articulos.join(", ") : "sin artículo específico"}
          </p>
          {n.razon_aplicacion && <p className="text-xs text-gray-600 mt-0.5">{n.razon_aplicacion}</p>}
          <p className="text-[11px] text-gray-500 mt-1">
            Consultada {formatDate(n.consulted_at)}
            {n.verification_status === "validada" && n.verified_at && <> · verificada {formatDate(n.verified_at)}{n.verified_by && ` por ${n.verified_by}`}</>}
            {n.url_oficial ? (
              <> · <a href={n.url_oficial} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`norma-url-${i}`}>Fuente oficial</a></>
            ) : " · sin enlace oficial registrado"}
          </p>
        </li>
      ))}
      <li className="text-[11px] text-gray-400">Solo se citan normas de la lista blanca. La pertinencia es interpretación de IA a confirmar.</li>
    </ul>
  );
}

function List({ items, testId }) {
  return <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700" data-testid={testId}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

function PendingState({ view }) {
  return (
    <section className="mt-6 border border-gray-200 bg-white p-6" data-testid="diagnosis-view-unavailable">
      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">Diagnóstico en pantalla</p>
      <p className="text-sm text-gray-700">
        {view.reason === "pendiente_revision"
          ? `El diagnóstico v${view.pending_version} fue generado automáticamente y está pendiente de revisión humana. Lo verás aquí completo en cuanto el revisor lo apruebe.`
          : "Todavía no hay diagnóstico para este caso."}
      </p>
    </section>
  );
}

export function DiagnosisView({ caseId, refreshKey, onRegenerated }) {
  const [budgetTick, setBudgetTick] = useState(0);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState(null);

  const load = useCallback(async () => {
    const [v, c] = await Promise.all([
      api.get(`/cases/${caseId}/diagnosis/view`).then((r) => r.data).catch(() => null),
      api.get(`/cases/${caseId}/comparables`).then((r) => r.data).catch(() => null),
    ]);
    setView(v);
    setSearch(c?.search || null);
  }, [caseId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!view) return null;
  if (!view.available) return view.reason === "sin_diagnostico" ? null : <PendingState view={view} />;
  const o = view.output || {};

  return (
    <section className="mt-6 border border-gray-200 bg-white" data-testid="diagnosis-view">
      <div className="p-6 border-b border-gray-200 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-primary font-medium">Diagnóstico precontractual · v{view.version}</p>
          <h2 className="font-serif text-2xl text-gray-900 mt-1" data-testid="diagnosis-classification">{CLASS_LABEL[o.preliminary_classification] || "Diagnóstico"}</h2>
          <p className="text-xs text-gray-500 mt-1">
            Generado {formatDate(view.created_at)} sobre borrador v{view.intake_version_used || "—"}
            {view.download_document_id && " · es la misma versión de la descarga"}
          </p>
        </div>
        <Button asChild variant="outline" className="rounded-sm h-10 border-primary text-primary" data-testid="diagnosis-open-checklist">
          <Link to={`/cases/${caseId}/checklist`}><ClipboardList className="w-4 h-4 mr-2" />Checklist precontractual</Link>
        </Button>
      </div>
      <div className="px-6 pt-4 space-y-3">
        <ReviewBadge review={view.review} />
        <DiagnosisRegenCard caseId={caseId} regen={view.budget_regen} onRegenerated={() => { load(); onRegenerated?.(); }} />
        <DiagnosisCompare caseId={caseId} refreshKey={refreshKey} />
        {view.stale && (
          <div className="border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3 flex gap-2" data-testid="diagnosis-stale-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Modificaste el borrador después de este diagnóstico (ahora v{view.current_intake_version}). Este análisis corresponde a la v{view.intake_version_used} y puede estar desactualizado.
          </div>
        )}
      </div>
      <div className="px-6 pb-6">
        <Block title="Resumen" testId="diagnosis-summary"><p className="text-sm text-gray-800 leading-relaxed">{o.executive_summary?.content}</p></Block>
        <Block title="Modalidad sugerida (preliminar)" testId="diagnosis-modality">
          <p className="text-sm font-medium text-gray-900">{MOD_LABEL[o.preliminary_modality?.modalidad_sugerida] || o.preliminary_modality?.modalidad_sugerida}</p>
          <p className="text-xs text-gray-600 mt-1">{o.preliminary_modality?.justificacion}</p>
        </Block>
        <Block title={`Comparables SECOP (${view.comparables.length})`} testId="diagnosis-comparables">
          {o.comparable_analysis?.content && <p className="text-sm text-gray-700 mb-3">{o.comparable_analysis.content}</p>}
          <MarketSummaryChart comparables={view.comparables} budget={view.case_budget_cop}
            onSaveBudget={view.can_edit_budget ? async (v) => { await api.post(`/cases/${caseId}/budget`, { valor_estimado_cop: v }); load(); setBudgetTick((t) => t + 1); } : undefined} />
          <BudgetHistory caseId={caseId} refreshKey={`${refreshKey}-${budgetTick}`} />
          <div className="mt-4" />
          <ComparablesTable items={view.comparables} search={search} />
        </Block>
        <Block title="Riesgos preliminares" testId="diagnosis-risks"><Risks risks={o.preliminary_risks || []} /></Block>
        <Block title="Fuentes normativas aplicadas" testId="diagnosis-normas"><Normas normas={view.normas_trazadas || []} /></Block>
        {o.missing_data?.length > 0 && <Block title="Información pendiente" testId="diagnosis-missing"><List items={o.missing_data} /></Block>}
        {o.preguntas_aclaracion?.length > 0 && <Block title="Preguntas antes del Kit Documental" testId="diagnosis-questions"><List items={o.preguntas_aclaracion} /></Block>}
        <Block title="Siguiente paso recomendado" testId="diagnosis-next-step"><p className="text-sm text-gray-800">{o.recommended_next_step}</p></Block>
        {o.limitations?.length > 0 && <Block title="Limitaciones" testId="diagnosis-limitations"><List items={o.limitations} /></Block>}
      </div>
    </section>
  );
}
